import axios from 'axios';
import { API_ENDPOINTS } from '../lib/constants/apiEndpoints';
import { ITokenService, TokenRequest } from '../types/auth';
import { isTransientHttpError } from '@/utils/httpErrors';

const sleep = (ms: number) => new Promise<void>((resolve) => setTimeout(resolve, ms));

// Jitter is used only for retry timing (not for any security/identity purpose).
// Uses cryptographically secure random number generator to avoid security hotspots.
const getJitterMs = (maxExclusiveMs: number): number => {
    if (maxExclusiveMs <= 0) return 0;

    const cryptoObj = globalThis.crypto;
    if (!cryptoObj?.getRandomValues) {
        throw new Error('Crypto.getRandomValues is not available in this environment');
    }

    const buf = new Uint32Array(1);
    cryptoObj.getRandomValues(buf);
    return buf[0] % maxExclusiveMs;
};

export class TokenService implements ITokenService {
    private readonly baseUrl = import.meta.env.VITE_PORTAL_API_URL || "";

    async getToken(microsoftToken: string, request: TokenRequest): Promise<string> {
        // Validate request
        if (!microsoftToken) {
            throw new Error('Microsoft token is required');
        }
        if (!request.clientId || !request.productId) {
            throw new Error('clientId and productId are required');
        }

        // Rolling deploys can briefly cause 5xx/network errors. Retry a few times with backoff.
        const maxAttempts = 4;
        const baseDelayMs = 400;

        let lastError: unknown;
        for (let attempt = 1; attempt <= maxAttempts; attempt++) {
            try {
                const response = await axios.post(`${this.baseUrl}${API_ENDPOINTS.AUTH.TOKEN}`, request, {
                    headers: {
                        'X-JWT-Assertion': microsoftToken
                    }
                });
                return response.data;
            } catch (error) {
                lastError = error;

                // Only retry transient errors (network/5xx/timeout/429/etc.)
                if (!isTransientHttpError(error) || attempt === maxAttempts) {
                    console.error('Error in token service:', error);
                    throw error;
                }

                const jitter = getJitterMs(200);
                const delay = baseDelayMs * attempt + jitter;
                console.warn(`Token request failed (attempt ${attempt}/${maxAttempts}). Retrying in ${delay}ms...`, error);
                await sleep(delay);
            }
        }

        // Should be unreachable
        throw lastError instanceof Error ? lastError : new Error('Failed to fetch token');
    }
} 